import { FC, useCallback, useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Box from "@mui/material/Box";
import CardMedia from "@mui/material/CardMedia";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { FeedbackType } from "../../../API";
import { IRecentEventsTableRow } from "../../../pages/data-hub/components/models/common/events.models";
import FeedbackDialogForm from "../../../pages/data-hub/components/models/recentEvents/FeedbackDialogForm";
import FeedbackThumbs from "../../../pages/data-hub/components/models/recentEvents/FeedbackThumbs";
import CardMediaVideoPlayerWithSpeedControls from "../../../pages/media-viewer/components/player/CardMediaVideoPlayerWithSpeedControls";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import CustomIconButton from "../button/CustomIconButton";
import CloseDialogIcon from "../icons/CloseDialogIcon";
import { allowedFileTypes, getFileExtensionFromSignedUrl } from "./fileHelper";
import { useHasRoleAccess } from "../../hooks/useCheckRoleAccess";

interface MediaCardDialogProps {
  rowData: IRecentEventsTableRow;
  signedUrl: string;
  open: boolean;
  fullWidth?: boolean;
  submitFeedbackLoading?: boolean;
  onClose: () => void;
  onFeedbackSubmitClick: (
    params: IRecentEventsTableRow,
    mitigatedRisk: string[]
  ) => void;
  children?: React.ReactNode;
}

const MIN_SCALE = 1;
const MAX_SCALE = 3;
const SCALE_STEP = 0.2;

const MediaCardDialogNew: FC<MediaCardDialogProps> = ({
  rowData,
  signedUrl,
  open,
  fullWidth,
  submitFeedbackLoading,
  onClose,
  onFeedbackSubmitClick,
  children,
}) => {
  const theme = useTheme();
  const isDeniedAccess = useHasRoleAccess("readonly");

  const { width: screenWidth } = useWindowDimensions();

  const [isPositiveFeedback, setIsPositiveFeedback] = useState(
    rowData.feedback === FeedbackType.POSITIVE
  );
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const positionRef = useRef({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const boxRectRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const handleBoxRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const rect = node.getBoundingClientRect();

        boxRectRef.current = { width: rect.width, height: rect.height };
      }
    },
    [scale]
  );

  const updateImageTransform = useCallback(() => {
    if (imageRef.current) {
      imageRef.current.style.transform = `scale(${scale}) translate(${positionRef.current.x}px, ${positionRef.current.y}px)`;
    }
  }, [scale]);

  useEffect(() => {
    updateImageTransform();
  }, [scale, updateImageTransform]);

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging) return;

    const newX = positionRef.current.x + event.movementX;
    const newY = positionRef.current.y + event.movementY;

    const { width, height } = boxRectRef.current;
    const minX = -width / 2;
    const maxX = width / 2;
    const minY = -height / 2;
    const maxY = height / 2;

    positionRef.current = {
      x: Math.max(minX, Math.min(maxX, newX)),
      y: Math.max(minY, Math.min(maxY, newY)),
    };

    updateImageTransform();
  };

  const getMaxWidth = () => {
    if (fullWidth) {
      return "100%";
    }

    if (screenWidth > theme.breakpoints.values.xxl) {
      return theme.breakpoints.values.xl + "px";
    }

    return theme.breakpoints.values.lg + "px";
  };

  const fileExtension = getFileExtensionFromSignedUrl(signedUrl);

  const isImage = allowedFileTypes.images.some(
    (allowedImageExtension): boolean => {
      return allowedImageExtension === fileExtension;
    }
  );

  const isVideo = allowedFileTypes.videos.some(
    (allowedVideoExtension): boolean => {
      return allowedVideoExtension === fileExtension;
    }
  );

  const memoizedThumbUpClick = useCallback((): void => {
    setIsPositiveFeedback(true);
  }, [rowData]);

  const memoizedThumbsDownClickCallback = useCallback((): void => {
    rowData.onThumbsDownClick &&
      rowData.onThumbsDownClick(rowData.rowId, rowData, true);

    setIsPositiveFeedback(false);
  }, [rowData.rowId, rowData]);

  const handleZoomIn = useCallback((): void => {
    setScale(prev => Math.min(prev + SCALE_STEP, MAX_SCALE));

    updateImageTransform();
  }, []);

  const handleZoomOut = useCallback((): void => {
    setScale(prev => Math.max(prev - SCALE_STEP, MIN_SCALE));
  }, []);

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          margin: 0,
          maxWidth: getMaxWidth(),
          maxHeight: getMaxWidth(),
          width: "100%",
          height: "100%",
          borderRadius: fullWidth ? "0" : "8px",
        },
      }}
    >
      <CloseDialogIcon onClose={onClose} />
      {children ? (
        children
      ) : (
        <DialogContent
          sx={{
            display: "flex",
            padding: "0px",
            width: "100%",
            height: "100%",
            overflowY: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "auto",
              minWidth: "50%",
              position: "relative",
            }}
          >
            {isImage && open && (
              <Box
                ref={handleBoxRef}
                sx={{
                  width: fullWidth ? "100vw" : "auto",
                  height: fullWidth ? "100vh" : "calc(100vh - 64px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  background: theme.palette.background.default,
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <CardMedia
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    display: "block",
                    margin: "auto",
                    userSelect: "none",
                    cursor: isDragging ? "grabbing" : "grab",
                  }}
                  src={signedUrl}
                  component="img"
                  ref={imageRef}
                  onMouseDown={handleMouseDown}
                  draggable={false}
                />
              </Box>
            )}
            {isVideo && open && (
              <CardMediaVideoPlayerWithSpeedControls
                sx={{
                  height: fullWidth ? "100vh" : "calc(100vh - 64px)",
                }}
                controls
                src={signedUrl}
                autoPlay
              />
            )}
            {isImage && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  right: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  padding: "8px",
                  zIndex: 10,
                }}
              >
                <CustomIconButton
                  onClick={handleZoomIn}
                  disabled={scale >= MAX_SCALE}
                >
                  <AddIcon />
                </CustomIconButton>
                <CustomIconButton
                  onClick={handleZoomOut}
                  disabled={scale <= MIN_SCALE}
                >
                  <RemoveIcon />
                </CustomIconButton>
              </Box>
            )}
          </Box>
          {!isDeniedAccess && (
            <Box
              sx={{
                padding: "2em",
                paddingBottom: 0,
                borderLeft: `1px solid ${theme.palette.grey[200]}`,
                minWidth: "280px",
              }}
            >
              <Typography variant="subtitle2" color="otherTextTertiary.main">
                FEEDBACK
              </Typography>

              <FeedbackThumbs
                disabled
                params={rowData}
                onThumbUpClick={memoizedThumbUpClick}
                onThumbsDownClick={memoizedThumbsDownClickCallback}
              />

              {(rowData.feedback === FeedbackType.POSITIVE ||
                isPositiveFeedback) && (
                <>
                  <Tooltip
                    title={
                      <Typography variant="body2Regular">
                        Feedback and Feedback details are available on the
                        Recent Activities (Beta) page
                      </Typography>
                    }
                    placement="top-start"
                    arrow
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="otherTextTertiary.main"
                      >
                        MITIGATED RISKS
                      </Typography>
                      <FeedbackDialogForm
                        isDisabled
                        submitLoading={submitFeedbackLoading}
                        rowData={rowData}
                        onSubmitClick={onFeedbackSubmitClick}
                      />
                    </Box>
                  </Tooltip>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default MediaCardDialogNew;
